import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleTypeahead } from '../BubbleUI';
import { Brand, BrandAttribute, BrandEventHighlight, BrandLevel, BrandSegment } from '@go-shared/types/brand';
import { updateBrand } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';
import { ObjectId } from 'bson';

interface BrandEventLibraryFormDataBase {
  segments: BrandSegment[];
  levels: BrandLevel[];
  attributes: BrandAttribute[];
  eventHighlights: BrandEventHighlight[];
}

type BrandEventLibraryFormData = BrandEventLibraryFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandEventLibraryFormError>;
};

type BrandEventLibraryFormError = { [K in keyof BrandEventLibraryFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandEventLibraryFormData {
  return {
    segments: brand ? brand.segments : [],
    levels: brand ? brand.levels : [],
    attributes: brand ? brand.attributes : [],

    eventHighlights: brand?.eventHighlights ?? [],
    _dirty: false,
    _errors: {},
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandEventLibraryForm(props: BrandFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandEventLibraryFormData>(
    DEFAULT_FORM_DATA(props.edit)
  );

  const segmentOpts = formData.segments.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const levelOpts = formData.levels.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const attributeOpts = formData.attributes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const eventHighlightsOpts = formData.eventHighlights.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandEventLibraryFormDataBase>(
    key: T,
    val: BrandEventLibraryFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,

          segments: formData.segments,
          levels: formData.levels,
          attributes: formData.attributes,

          eventHighlights: formData.eventHighlights,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Segments" className="col-span-3">
        <BubbleTypeahead
          block
          options={segmentOpts}
          placeholder="Enter segment name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={segmentOpts.length > 0}
          suggestionsText="No similar segments found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const arIdx = formData.segments.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newSegs = [...formData.segments];
                  newSegs.splice(arIdx, 1, { ...opt.item, name: val });
                  changeForm('segments', newSegs);
                }}
              />
              <span className="text-xs font-semibold uppercase text-navy mr-3">Color</span>
              <BubbleInput
                type="color"
                value={opt.item.color}
                onValue={val => {
                  const arIdx = formData.segments.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newSegs = [...formData.segments];
                  newSegs.splice(arIdx, 1, { ...opt.item, color: val });
                  changeForm('segments', newSegs);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('segments', [
              ...formData.segments,
              { _id: new ObjectId().toHexString(), name: val, color: '#000000' },
            ])
          }
          onTrash={val =>
            changeForm(
              'segments',
              formData.segments.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Levels" className="col-span-3">
        <BubbleTypeahead
          block
          options={levelOpts}
          placeholder="Enter level name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={levelOpts.length > 0}
          suggestionsText="No similar levels found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const lIdx = formData.levels.findIndex(l => l._id === opt.item._id);
                  if (lIdx < 0) return;
                  const newLevels = [...formData.levels];
                  newLevels.splice(lIdx, 1, { ...opt.item, name: val });
                  changeForm('levels', newLevels);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('levels', [
              ...formData.levels,
              { _id: new ObjectId().toHexString(), name: val },
            ])
          }
          onTrash={val =>
            changeForm(
              'levels',
              formData.levels.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Attributes" className="col-span-3">
        <BubbleTypeahead
          block
          options={attributeOpts}
          placeholder="Enter attribue name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={attributeOpts.length > 0}
          suggestionsText="No similar attributes found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const attrIdx = formData.attributes.findIndex(a => a._id === opt.item._id);
                  if (attrIdx < 0) return;
                  const newAttrs = [...formData.attributes];
                  newAttrs.splice(attrIdx, 1, { ...opt.item, name: val });
                  changeForm('attributes', newAttrs);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('attributes', [
              ...formData.attributes,
              { _id: new ObjectId().toHexString(), name: val },
            ])
          }
          onTrash={val =>
            changeForm(
              'attributes',
              formData.attributes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>

      <BubbleFormGroup
        label="Event Highlights"
        error={formData._errors.eventHighlights}
        className="col-span-3"
      >
        <BubbleTypeahead
          block
          options={eventHighlightsOpts}
          placeholder="Enter event hightlight name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={eventHighlightsOpts.length > 0}
          suggestionsText="No similar event hightlights found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const eventHighlightIdx = formData.eventHighlights.findIndex(
                    s => s._id === opt.item._id
                  );
                  if (eventHighlightIdx < 0) return;
                  const newEventHighlight = [...formData.eventHighlights];
                  newEventHighlight.splice(eventHighlightIdx, 1, { ...opt.item, name: val });
                  changeForm('eventHighlights', newEventHighlight);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.eventHighlights.find(s => s.name === val);
            if (!exists) {
              changeForm('eventHighlights', [
                ...formData.eventHighlights,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'eventHighlights',
              formData.eventHighlights.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
